import { useFlag } from '@calendly/feature-check-react'

import { isPast } from 'components/meetings/utils'
import {
  EventStatus,
  noRecapAvailableStateErrors,
  noRecapAvailableStateStatuses,
  recapProcessingStateStatuses,
} from 'features/meetings/notetaker_constants'

import { useGetNotetakerToggleState } from './use_get_notetaker_toggle_state'

/** UI display modes for notetaker/recap control */
export const NotetakerDisplayMode = {
  HIDDEN: 'hidden',
  TOGGLE_ON: 'toggle_on',
  TOGGLE_OFF: 'toggle_off',
  RECAP_PROCESSING: 'recap_processing',
  RECAP_BUTTON: 'recap_button',
  NO_RECAP_AVAILABLE: 'no_recap_available',
  RECAP_ERROR: 'recap_error',
} as const

export type NotetakerDisplayMode = typeof NotetakerDisplayMode[keyof typeof NotetakerDisplayMode]

/** Statuses indicating the notetaker is actively recording (not yet ended) */
const ACTIVE_RECORDING_STATUSES = [EventStatus.MEETING_IN_PROGRESS, EventStatus.PRE_JOIN]

/** Complete state object returned by useNotetakerState */
export interface NotetakerState {
  displayMode: NotetakerDisplayMode
  noRecapTooltipKey?: string
  toggleState: ReturnType<typeof useGetNotetakerToggleState>
}

/**
 * Gets the tooltip key for "no recap available" state based on error or status.
 * @param event - Meeting event object
 * @returns Tooltip translation key or undefined
 */
function getNoRecapTooltipKey(event: any): string | undefined {
  const { status, error } = event?.meetingIntelligence ?? {}
  return (error && noRecapAvailableStateErrors[error]) || (status && noRecapAvailableStateStatuses[status])
}

/**
 * Determines if recap error state should be shown.
 * Replicates production's shouldShowRecapBanner + RecapIndicator fallback logic.
 *
 * Shows error when meeting has ended AND status is not in "in-progress" states.
 * (Other statuses like COMPLETED, SUMMARIZING are handled by earlier checks in determineDisplayMode)
 *
 * @param event - Meeting event object
 * @param status - Meeting intelligence status
 * @returns true if recap error should be shown
 */
function shouldShowRecapError(event: any, status: string | undefined): boolean {
  if (!status || !event?.meetingIntelligence) return false

  const hasEnded = event.endTime ? new Date(event.endTime).getTime() < Date.now() : isPast(event)

  const inProgressStatuses = [
    'SCHEDULED', 'PREJOIN', 'PRE_JOIN', 'MEETING_IN_PROGRESS', 'MEETINGINPROGRESS', 'QUOTA_EXCEEDED', 'QUOTAEXCEEDED',
  ]
  const isNotInProgress = !inProgressStatuses.includes(status.toUpperCase())

  return hasEnded && isNotInProgress
}

/**
 * State machine that determines which UI component to display.
 * Priority order matters - first matching condition wins.
 *
 * Priority logic:
 * 1. Feature flag - all MI UI hidden if meeting_intelligence flag is off
 * 2. No recap available states (specific errors/statuses) - shown with tooltip
 * 3. COMPLETED status - show recap button
 * 4. Processing states (MEETING_ENDED, SUMMARIZING) - show processing banner
 * 5. Recap error - show error for ended meetings with unrecognized statuses
 * 6. Past meetings - hide if no MI or status is recognized
 * 7. User settings - hide if showNotetaker is false
 * 8. Toggle states - show toggle for current/future meetings
 *
 * @param event - Meeting event object
 * @param toggleState - State from useGetNotetakerToggleState hook
 * @param hasMeetingIntelligence - Whether meeting_intelligence feature flag is enabled
 * @returns Display mode and optional tooltip key
 */
function determineDisplayMode(
  event: any,
  { showNotetaker, notetakerAdded, isLoading }: ReturnType<typeof useGetNotetakerToggleState>,
  hasMeetingIntelligence: boolean
): { displayMode: NotetakerDisplayMode; noRecapTooltipKey?: string } {
  const status = event?.meetingIntelligence?.status
  const noRecapTooltipKey = getNoRecapTooltipKey(event)

  if (!hasMeetingIntelligence) return { displayMode: NotetakerDisplayMode.HIDDEN }

  // Priority: Specific recap states
  if (noRecapTooltipKey) return { displayMode: NotetakerDisplayMode.NO_RECAP_AVAILABLE, noRecapTooltipKey }
  if (status === EventStatus.COMPLETED) return { displayMode: NotetakerDisplayMode.RECAP_BUTTON }
  if (status && recapProcessingStateStatuses.includes(status)) return { displayMode: NotetakerDisplayMode.RECAP_PROCESSING }
  if (shouldShowRecapError(event, status)) return { displayMode: NotetakerDisplayMode.RECAP_ERROR }

  // Past meetings: Keep toggle visible if notetaker is actively recording
  // This handles edge cases where meetings go overtime or users need to see notetaker state
  const isActivelyRecording = status && ACTIVE_RECORDING_STATUSES.includes(status)

  if (isPast(event) && !isActivelyRecording) return { displayMode: NotetakerDisplayMode.HIDDEN }

  // Current/future meetings: Show toggle based on state
  if (!showNotetaker && !isLoading) return { displayMode: NotetakerDisplayMode.HIDDEN }
  if (notetakerAdded) return { displayMode: NotetakerDisplayMode.TOGGLE_ON }
  if (showNotetaker) return { displayMode: NotetakerDisplayMode.TOGGLE_OFF }

  return { displayMode: NotetakerDisplayMode.HIDDEN }
}

/**
 * Centralized hook for notetaker/recap state management.
 * Determines which UI component to display based on meeting intelligence status and toggle state.
 * @param event - Meeting event object
 * @returns Notetaker state with display mode, tooltip key, and toggle state
 */
export function useNotetakerState(event: any): NotetakerState {
  const hasMeetingIntelligence = useFlag('meeting_intelligence')
  const toggleState = useGetNotetakerToggleState(event)
  const { displayMode, noRecapTooltipKey } = determineDisplayMode(event, toggleState, hasMeetingIntelligence)

  return { displayMode, noRecapTooltipKey, toggleState }
}
